const PENDING_KEY = "pwaInstallPendingSync";
const INSTALLED_DISPLAY_MODES = ["standalone", "fullscreen", "minimal-ui", "window-controls-overlay"];

function isUserLoggedIn() {
  return document.body?.dataset?.userLoggedIn === "true";
}

function currentUserId() {
  return document.body?.dataset?.userId || "";
}

function syncedStorageKey() {
  const userId = currentUserId();
  return userId ? `pwaInstallationSynced:${userId}` : null;
}

function isInstalledPwa() {
  for (const mode of INSTALLED_DISPLAY_MODES) {
    if (window.matchMedia?.(`(display-mode: ${mode})`).matches) return true;
  }

  return window.navigator?.standalone === true;
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || "";
}

function documentReady() {
  return document.readyState !== "loading" && document.body;
}

export function hasPendingPwaInstallSync() {
  return localStorage.getItem(PENDING_KEY) === "true";
}

export function markPwaInstallPendingSync() {
  localStorage.setItem(PENDING_KEY, "true");
}

export function clearPwaInstallPendingSync() {
  localStorage.removeItem(PENDING_KEY);
}

export function hasSyncedPwaInstallationForCurrentUser() {
  const key = syncedStorageKey();
  return Boolean(key && localStorage.getItem(key) === "true");
}

export function markSyncedPwaInstallationForCurrentUser() {
  const key = syncedStorageKey();
  if (key) localStorage.setItem(key, "true");
}

function shouldAttemptRecording({ force = false } = {}) {
  if (force) return true;
  if (isInstalledPwa()) return true;
  return hasPendingPwaInstallSync();
}

function markInstallDetectedIfNeeded({ force = false } = {}) {
  if (force || isInstalledPwa()) {
    markPwaInstallPendingSync();
  }
}

export async function recordPwaInstallation({ force = false } = {}) {
  if (!documentReady()) return;

  if (!isUserLoggedIn()) {
    markInstallDetectedIfNeeded({ force });
    return;
  }

  if (hasSyncedPwaInstallationForCurrentUser()) return;
  if (!shouldAttemptRecording({ force })) return;

  try {
    const response = await fetch("/pwa_installation", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      keepalive: true,
    });

    if (response.ok) {
      markSyncedPwaInstallationForCurrentUser();
      clearPwaInstallPendingSync();
    }
  } catch {
    // Retry on next page load
  }
}

export async function recordPwaInstallationIfNeeded() {
  return recordPwaInstallation({ force: false });
}

export function wirePwaInstallationRecorder() {
  if (typeof window === "undefined" || window.__aquiferPwaInstallationRecorderWired) return;

  window.__aquiferPwaInstallationRecorderWired = true;
  window.addEventListener("appinstalled", () => {
    recordPwaInstallation({ force: true });
  });
}
